import React, { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, ChevronLeft, Eye, EyeOff, Globe, Loader2, LockKeyhole, Mail, ShieldCheck } from 'lucide-react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  apiErrorCode,
  communicationsService
} from '../services/communicationsService'
import type { Locale } from '../types/communications'
import AnimatedTrackingBackdrop from './AnimatedTrackingBackdrop'
import SiniBrand from './SiniBrand'

const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation()
  return <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 p-5">
    <AnimatedTrackingBackdrop />
    <div className="absolute right-5 top-5 z-20 flex rounded-lg border border-slate-200 bg-white/90 p-1 shadow-sm">
      <Globe size={15} className="ml-2 mr-1 self-center text-slate-500" />
      {(['en', 'fr'] as Locale[]).map(locale => <button key={locale} onClick={() => i18n.changeLanguage(locale)}
        className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase ${
          i18n.language?.startsWith(locale) ? 'bg-blue-50 text-blue-700' : 'text-slate-500'
        }`}>{locale}</button>)}
    </div>
    <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white/95 p-7 shadow-xl backdrop-blur">
      <div className="mb-6 flex justify-center"><SiniBrand /></div>
      {children}
      <div className="mt-6 text-center">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
          <ChevronLeft size={13} />{t('communications.recovery.backToLogin')}
        </Link>
      </div>
    </div>
  </div>
}

export const ForgotPasswordPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [identifier, setIdentifier] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [failed, setFailed] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setFailed(false)
    try {
      await communicationsService.requestPasswordReset(identifier, i18n.language?.startsWith('fr') ? 'fr' : 'en')
      setSent(true)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return <PublicShell>
    {sent ? <div className="text-center">
      <CheckCircle2 className="mx-auto text-emerald-600" size={42} />
      <h1 className="mt-4 text-xl font-bold text-slate-900">{t('communications.recovery.checkInbox')}</h1>
      <p className="mt-2 text-sm leading-6 text-slate-600">{t('communications.recovery.genericForgot')}</p>
    </div> : <form onSubmit={submit} className="space-y-5">
      <div className="text-center">
        <Mail className="mx-auto text-blue-600" size={34} />
        <h1 className="mt-3 text-xl font-bold text-slate-900">{t('communications.recovery.forgotTitle')}</h1>
        <p className="mt-2 text-sm text-slate-500">{t('communications.recovery.forgotDescription')}</p>
      </div>
      {failed && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{t('communications.recovery.requestFailed')}</div>}
      <label className="block text-sm font-medium text-slate-700">
        {t('communications.recovery.emailOrUsername')}
        <input required autoComplete="username" value={identifier} onChange={event => setIdentifier(event.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500" />
      </label>
      <button disabled={busy || !identifier.trim()}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}{t('communications.recovery.sendLink')}
      </button>
    </form>}
  </PublicShell>
}

const passwordChecks = (value: string) => [
  value.length >= 12,
  value.length <= 128,
  /[a-z]/.test(value),
  /[A-Z]/.test(value),
  /\d/.test(value),
  /[^A-Za-z0-9]/.test(value)
]

export const ResetPasswordPage: React.FC = () => {
  const { token = '' } = useParams()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const checks = passwordChecks(password)
  const valid = checks.every(Boolean) && password === confirmation

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid) return setError(t('communications.recovery.passwordInvalid'))
    setBusy(true)
    setError('')
    try {
      await communicationsService.resetPassword(token, password, i18n.language?.startsWith('fr') ? 'fr' : 'en')
      setDone(true)
    } catch (requestError) {
      setError(apiErrorCode(requestError) === 'INVALID_RESET_TOKEN'
        ? t('communications.recovery.invalidResetLink')
        : t('communications.recovery.resetFailed'))
    } finally {
      setBusy(false)
    }
  }

  return <PublicShell>
    {done ? <div className="text-center">
      <CheckCircle2 className="mx-auto text-emerald-600" size={42} />
      <h1 className="mt-4 text-xl font-bold">{t('communications.recovery.resetDone')}</h1>
      <p className="mt-2 text-sm text-slate-500">{t('communications.recovery.resetDoneHint')}</p>
      <button onClick={() => navigate('/login')} className="mt-5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white">
        {t('communications.recovery.signIn')}
      </button>
    </div> : <form onSubmit={submit} className="space-y-4">
      <div className="text-center">
        <LockKeyhole className="mx-auto text-blue-600" size={34} />
        <h1 className="mt-3 text-xl font-bold">{t('communications.recovery.resetTitle')}</h1>
        <p className="mt-2 text-sm text-slate-500">{t('communications.recovery.resetDescription')}</p>
      </div>
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</div>}
      {[
        ['password', t('communications.recovery.newPassword'), password, setPassword],
        ['confirmation', t('communications.recovery.confirmPassword'), confirmation, setConfirmation]
      ].map(([id, label, value, setter]) => <label key={String(id)} className="block text-sm font-medium text-slate-700">
        {String(label)}
        <div className="relative mt-2">
          <input type={show ? 'text' : 'password'} autoComplete={id === 'password' ? 'new-password' : 'new-password'}
            value={String(value)} onChange={event => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 pr-11 text-sm outline-none focus:border-blue-500" />
          <button type="button" onClick={() => setShow(current => !current)} className="absolute right-3 top-3 text-slate-400">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </label>)}
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-50 p-3 text-[10px]">
        {[
          'lengthMin', 'lengthMax', 'lowercase', 'uppercase', 'number', 'symbol'
        ].map((key, index) => <span key={key} className={checks[index] ? 'text-emerald-700' : 'text-slate-500'}>
          {checks[index] ? '✓' : '○'} {t(`communications.recovery.policy.${key}`)}
        </span>)}
        <span className={password && confirmation && password === confirmation ? 'text-emerald-700' : 'text-slate-500'}>
          {password && confirmation && password === confirmation ? '✓' : '○'} {t('communications.recovery.policy.match')}
        </span>
      </div>
      <button disabled={busy || !valid}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}{t('communications.recovery.resetAction')}
      </button>
    </form>}
  </PublicShell>
}

export const VerifyEmailPage: React.FC = () => {
  const { token = '' } = useParams()
  const { t } = useTranslation()
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading')

  useEffect(() => {
    communicationsService.confirmEmail(token).then(() => setState('done')).catch(() => setState('error'))
  }, [token])

  return <PublicShell><div className="text-center">
    {state === 'loading' && <Loader2 className="mx-auto animate-spin text-blue-600" size={38} />}
    {state === 'done' && <CheckCircle2 className="mx-auto text-emerald-600" size={42} />}
    {state === 'error' && <ShieldCheck className="mx-auto text-red-500" size={42} />}
    <h1 className="mt-4 text-xl font-bold">{t(`communications.verification.${state}Title`)}</h1>
    <p className="mt-2 text-sm leading-6 text-slate-500">{t(`communications.verification.${state}Description`)}</p>
  </div></PublicShell>
}

export const NewsletterPreferencesPage: React.FC = () => {
  const { token: pathToken = '' } = useParams()
  const [search] = useSearchParams()
  const { t } = useTranslation()
  const token = pathToken || search.get('token') || ''
  const [state, setState] = useState<'ready' | 'busy' | 'done' | 'error'>(token ? 'ready' : 'error')
  const title = useMemo(() => state === 'done'
    ? t('communications.preferences.doneTitle')
    : state === 'error' ? t('communications.preferences.invalidTitle') : t('communications.preferences.title'), [state, t])

  const unsubscribe = async () => {
    setState('busy')
    try {
      await communicationsService.unsubscribe(token)
      setState('done')
    } catch {
      setState('error')
    }
  }

  return <PublicShell><div className="text-center">
    {state === 'done' ? <CheckCircle2 className="mx-auto text-emerald-600" size={42} /> : <Mail className="mx-auto text-blue-600" size={38} />}
    <h1 className="mt-4 text-xl font-bold">{title}</h1>
    <p className="mt-2 text-sm leading-6 text-slate-500">
      {t(state === 'done' ? 'communications.preferences.doneDescription'
        : state === 'error' ? 'communications.preferences.invalidDescription' : 'communications.preferences.description')}
    </p>
    {state === 'ready' && <button onClick={unsubscribe}
      className="mt-5 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white">
      {t('communications.preferences.unsubscribe')}
    </button>}
    {state === 'busy' && <Loader2 className="mx-auto mt-5 animate-spin text-blue-600" />}
  </div></PublicShell>
}
